export const REQUESTED_CONTEXT_STORAGE_KEY = 'artstash_requested_context';

export interface RequestedContext {
  accountId: string | null;
  workspaceId: string | null;
}

const CONTEXT_ROUTE_PATTERN = /^\/account\/([^/]+)\/workspaces\/([^/]+)\/?$/;
const ACCOUNT_ROUTE_PATTERN = /^\/account\/([^/]+)\/?$/;

function decodeRouteValue(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

export function buildContextPath(accountId: string, workspaceId: string): string {
  return `/account/${encodeURIComponent(accountId)}/workspaces/${encodeURIComponent(workspaceId)}`;
}

export function buildAccountPath(accountId: string): string {
  return `/account/${encodeURIComponent(accountId)}`;
}

export function getRequestedContext(
  search: string = window.location.search,
  pathname: string = window.location.pathname
): RequestedContext {
  const routeMatch = pathname.match(CONTEXT_ROUTE_PATTERN);
  if (routeMatch) {
    return {
      accountId: decodeRouteValue(routeMatch[1]),
      workspaceId: decodeRouteValue(routeMatch[2]),
    };
  }

  const accountRouteMatch = pathname.match(ACCOUNT_ROUTE_PATTERN);
  if (accountRouteMatch) {
    return {
      accountId: decodeRouteValue(accountRouteMatch[1]),
      workspaceId: null,
    };
  }

  const params = new URLSearchParams(search);
  return {
    accountId: params.get('accountId') || null,
    workspaceId: params.get('workspaceId') || null,
  };
}

export function preserveRequestedContext(
  search: string = window.location.search,
  pathname: string = window.location.pathname
): void {
  const { accountId, workspaceId } = getRequestedContext(search, pathname);
  const params = new URLSearchParams();

  if (accountId) params.set('accountId', accountId);
  if (workspaceId) params.set('workspaceId', workspaceId);

  const context = params.toString();
  if (context) {
    sessionStorage.setItem(REQUESTED_CONTEXT_STORAGE_KEY, context);
  } else {
    sessionStorage.removeItem(REQUESTED_CONTEXT_STORAGE_KEY);
  }
}

export function getRequestedWorkspaceId(
  requestedContext: RequestedContext,
  selectedAccountId: string | null
): string | null {
  if (requestedContext.accountId && selectedAccountId !== requestedContext.accountId) {
    return null;
  }
  return requestedContext.workspaceId;
}

export function consumeRequestedContextPath(): string {
  const storedContext = sessionStorage.getItem(REQUESTED_CONTEXT_STORAGE_KEY);
  sessionStorage.removeItem(REQUESTED_CONTEXT_STORAGE_KEY);

  if (!storedContext) return '/';

  const { accountId, workspaceId } = getRequestedContext(`?${storedContext}`, '/');
  if (accountId && workspaceId) return buildContextPath(accountId, workspaceId);

  const params = new URLSearchParams();
  if (accountId) params.set('accountId', accountId);
  if (workspaceId) params.set('workspaceId', workspaceId);
  const query = params.toString();
  return query ? `/?${query}` : '/';
}
